import { useCallback, useEffect, useState } from "react"
import type { AppShell } from "@/App"
import { invokeAction, listEncHistory, realEncNumber, type EncHistoryRow, type OrderRow } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { AlertTriangle, Ban, CheckCircle2 } from "lucide-react"

interface Props {
  shell: AppShell | null
  order: OrderRow
  /** Detalhe da encomenda: mostra "Já anulei no ETICDATA". Armazém: só aviso. */
  allowConfirm?: boolean
  /**
   * Vista do armazém (Logística): só o aviso, com o texto pedido pelo cliente
   * — sem as notas internas (ENC a anular, fornecedores…), que são para o gestor.
   */
  warehouse?: boolean
}

function fmt(ts: string | null | undefined): string {
  return ts ? new Date(ts).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : ""
}

/**
 * Aviso de encomenda alterada/cancelada no Shopify + ENCs antigas por anular
 * no ETICDATA (o ETICDATA não anula pela API — o cliente anula e confirma aqui).
 */
export function OrderChangeAlert({ shell, order, allowConfirm = false, warehouse = false }: Props) {
  const [history, setHistory] = useState<EncHistoryRow[]>([])
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!shell) return
    setHistory(await listEncHistory(shell, order.id))
  }, [shell, order.id])

  useEffect(() => {
    void load()
  }, [load, order.shopify_changed_at])

  async function confirm(row: EncHistoryRow) {
    if (!shell) return
    setBusy(row.id)
    try {
      const res = await invokeAction(shell, "confirm_enc_cancelled", { order_id: order.id, history_id: row.id })
      if (!res.success) throw new Error(res.error ?? "Falhou")
      await load()
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : String(err) })
    } finally {
      setBusy(null)
    }
  }

  async function confirmUpdated() {
    if (!shell) return
    setBusy("enc-update")
    try {
      const res = await invokeAction(shell, "confirm_enc_updated", { order_id: order.id })
      if (!res.success) throw new Error(res.error ?? "Falhou")
      shell.bridge.toast({ variant: "success", description: "ENC marcada como atualizada no ETICDATA." })
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : String(err) })
    } finally {
      setBusy(null)
    }
  }

  if (warehouse) return <WarehouseChangeNotice order={order} />

  const pending = history.filter((h) => h.status === "cancel_pending")
  const confirmed = history.filter((h) => h.status !== "cancel_pending")
  if (!order.cancelled_at && !order.change_alert && !order.enc_update_pending && history.length === 0) return null

  const encLabel = (h: EncHistoryRow) => realEncNumber(h.document_number) ?? h.document_number ?? "ENC"

  return (
    <div className="space-y-2">
      {order.cancelled_at ? (
        <div className="flex items-start gap-2 rounded-md border-2 border-red-500 bg-red-50 px-3 py-2 text-sm text-red-900">
          <Ban className="mt-0.5 size-4 shrink-0" />
          <div>
            <div className="font-bold">ENCOMENDA CANCELADA no Shopify · {fmt(order.cancelled_at)}</div>
            <div>Não separar nem expedir.{order.change_alert ? ` ${order.change_alert}` : ""}</div>
          </div>
        </div>
      ) : order.change_alert ? (
        <div className="flex items-start gap-2 rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <div>
            <div className="font-semibold">Encomenda alterada no Shopify · {fmt(order.shopify_changed_at)}</div>
            <div>{order.change_alert}</div>
          </div>
        </div>
      ) : null}

      {order.enc_update_pending && !order.cancelled_at && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span>
            <strong>Atualizar a ENC no ETICDATA:</strong> {order.enc_update_pending}
          </span>
          {allowConfirm && (
            <Button size="sm" variant="outline" disabled={busy === "enc-update"} onClick={() => void confirmUpdated()}>
              {busy === "enc-update" ? "A registar…" : "Já atualizei a ENC no ETICDATA"}
            </Button>
          )}
        </div>
      )}

      {pending.map((h) => (
        <div
          key={h.id}
          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-red-300 bg-red-50/60 px-3 py-2 text-sm text-red-900"
        >
          <span>
            <span className="font-mono font-semibold">{encLabel(h)}</span> — encomenda original{" "}
            {h.reason === "cancelled" ? "cancelada" : "substituída"} · <strong>anular no ETICDATA</strong>
          </span>
          {allowConfirm && (
            <Button size="sm" variant="outline" disabled={busy === h.id} onClick={() => void confirm(h)}>
              {busy === h.id ? "A registar…" : "Já anulei no ETICDATA"}
            </Button>
          )}
        </div>
      ))}

      {confirmed.map((h) => (
        <div key={h.id} className="flex items-center gap-2 text-xs text-muted-foreground">
          <CheckCircle2 className="size-3.5 text-emerald-600" />
          <span className="font-mono">{encLabel(h)}</span> anulada no ETICDATA · {fmt(h.confirmed_at)}
        </div>
      ))}
    </div>
  )
}

/**
 * Aviso ao armazém (texto do cliente):
 *  - cancelada → "ENCOMENDA CANCELADA" + repor no stock o que já foi separado;
 *  - alterada  → "CLIENTE ALTEROU ENCOMENDA" + suspensa até nova informação,
 *    enquanto a lista nova não chega; quando volta à separação, separar pela
 *    lista atual (as linhas alteradas vêm assinaladas).
 */
function WarehouseChangeNotice({ order }: { order: OrderRow }) {
  if (order.cancelled_at) {
    return (
      <div className="flex items-start gap-3 rounded-md border-2 border-red-600 bg-red-50 px-4 py-3 text-red-900">
        <Ban className="mt-0.5 size-5 shrink-0" />
        <div>
          <div className="text-base font-bold uppercase tracking-wide">Encomenda cancelada</div>
          <div className="text-sm">Caso exista material separado, repor o material no stock.</div>
          <div className="mt-1 text-xs text-red-700">{fmt(order.cancelled_at)}</div>
        </div>
      </div>
    )
  }
  if (!order.change_alert) return null
  const waitingNewList = order.current_step !== "logistica" || !!order.payment_pending_at
  return (
    <div className="flex items-start gap-3 rounded-md border-2 border-amber-500 bg-amber-50 px-4 py-3 text-amber-900">
      <AlertTriangle className="mt-0.5 size-5 shrink-0" />
      <div>
        <div className="text-base font-bold uppercase tracking-wide">Cliente alterou encomenda</div>
        <div className="text-sm">
          {waitingNewList
            ? "Encomenda suspensa até nova informação."
            : "Separar pela lista atual — as linhas alteradas estão assinaladas."}
        </div>
        <div className="mt-1 text-xs text-amber-700">{fmt(order.shopify_changed_at)}</div>
      </div>
    </div>
  )
}
